import React from 'react';
import { AxiosError, AxiosResponse } from 'axios';
import { Action, Column, Options } from 'material-table';
import { Button, createStyles, Grid, Icon, makeStyles, Typography } from '@material-ui/core';

import { ClientContext } from '../../ClientContext';
import { HomeContext } from '../../../Home/HomeContext';
import ClipperService from '../../../../Services/ClipperService';
import ClientClipperFiltersService from '../../../../Services/ClientClipperFiltersService';
import { ClipperModel } from '../../../../Models/ClipperModel';
import { ClientClipperModel, clientClipperModelInitial } from '../../../../Models/ClientClipperModel';
import { getTableColumn } from '../../../../shared/operators';
import { EditAction } from '../../../../shared/enum/EditAction';
import { PageLocation } from '../../../../shared/enum/PageLocation';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import { TableCommon } from '../../../../shared/components/TableCommon';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { ClientClipperContext } from '../ClientClipperContext';
import { ClientClipperModal } from '../components/ClientClipperModal';

const useStyles = makeStyles(() => createStyles({
    cardHeight: {
        overflow: 'hidden',
        maxHeight: '240px'
    },
}));

export function ClientClipper() {
    const classes = useStyles();

    const clientContext = React.useContext(ClientContext);
    const homeContext = React.useContext(HomeContext);

    const [clientClippersForTable, setClientClippersForTable] = React.useState<ClientClipperModel[]>([]);
    
    const [currentClientClipperIndex, setCurrentClientClipperIndex] = React.useState<number>(-1);
    const [clipperList, setClipperList] = React.useState<ClipperModel[]>([]);
    const [currentClipper, setCurrentClipper] = React.useState<ClipperModel | null>(null);
    const [currentClientClipper, setCurrentClientClipper] = React.useState<ClientClipperModel>(clientClipperModelInitial);
    const [isClipperModalOpen, setIsClipperModalOpen] = React.useState<boolean>(false);
    const [clipperEditMode, setClipperEditMode] = React.useState(false);

    const [isConfirmDeletionModalOpen, setIsConfirmDeletionModalOpen] = React.useState<boolean>(false);
    const [showTableRowPassword, setShowTableRowPassword] = React.useState<boolean[]>([]);

    React.useEffect(() => {
        if (clipperList.length == 0 
            && (homeContext.pageLocation === PageLocation.CLIENT_EDIT || homeContext.pageLocation === PageLocation.CLIENT_INPUT)) {
            ClipperService.clipperService()
                .getAll()
                .then((res: AxiosResponse) => {
                    const clippers = res.data.filter((c: ClipperModel) => c.active);
                    setClipperList(clippers);
                })
                .catch((error: AxiosError) => { homeContext.showError('Ops.. ocorreu um erro com Clipadora. <br/>', error); });
        }
    }, [homeContext.pageLocation]);

    React.useEffect(() => {
        const filteredClippers: ClientClipperModel[] = JSON.parse(JSON.stringify(clientContext.clientClippers))
            .filter((c: ClientClipperModel) => c.editAction != EditAction.DELETE);
        
        setClientClippersForTable([...filteredClippers]);
    }, [clientContext.clientClippers]);

    React.useEffect(() => {
        const showPasswordArray: boolean[] = [];
        clientClippersForTable.forEach(() => showPasswordArray.push(false));
        
        setShowTableRowPassword(showPasswordArray);
    }, [clientClippersForTable.length]);

    const clippersTableColumns: Column<ClientClipperModel>[] = [
        getTableColumn('Nome', 'clipper.name', 'string', 'left', undefined, 'asc'),
        getTableColumn('Login', 'login', 'string', 'left'),
        {
            title: 'Senha',
            type: 'string',
            sorting: false,
            filtering: false,
            align: 'left',
            render: (rowData: ClientClipperModel) => showTableClipperPassword(rowData)
        }
    ];
    
    const clippersTableOptions: Options<ClientClipperModel> = {
        filtering: true,
        sorting: true,
        showTitle: false,
        toolbar: false,
        headerStyle: {
            fontSize: '14px',
            whiteSpace: 'nowrap'
        },
        pageSizeOptions: [5, 10, 15],
        pageSize: 5,
    };
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const clippersTableActions: (Action<any> | ((rowData: any) => Action<any>))[] = [
        rowData => ({
            icon: showTableRowPassword[rowData.tableData.id] ? 'visibility' : 'visibility_off',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Visualizar senha',
            onClick: (_, rowData) => {
                const visiblePasswords = [...showTableRowPassword];
                visiblePasswords[rowData.tableData.id] = !visiblePasswords[rowData.tableData.id];
                setShowTableRowPassword(visiblePasswords);
            }
        }),
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar clipadora',
            onClick: (_, rowData) => { handleClipperTableEdit(rowData, rowData.tableData.id); }
        },
        {
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Apagar clipadora',
            onClick: (_, rowData) => { setCurrentClientClipperIndex(rowData.tableData.id); setIsConfirmDeletionModalOpen(true); },
        },
    ];

    const fetchClippingParametersForClipper = (clientClipper: ClientClipperModel) => {
        homeContext.setOpenLoadingDialog(true);

        ClientClipperFiltersService.clientClipperFiltersService(clientClipper.id)
            .getAll()
            .then((res: AxiosResponse) => setCurrentClientClipper({...clientClipper, clippingFilters: res.data}))
            .catch((error: AxiosError) => homeContext.showError('Ops.. ocorreu um erro com os filtros de clipping automático dessa clipadora. <br/>', error))
            .finally(() => homeContext.setOpenLoadingDialog(false));
    };

    const showTableClipperPassword = (rowData: ClientClipperModel) => {
        if (showTableRowPassword[rowData.id])
            return rowData.password;
        else
            return rowData.password.split('').map(() => '*');
    };

    const handleClipperTableEdit = (clientClipper: ClientClipperModel, index: number) => {
        setClipperEditMode(true);
        setCurrentClientClipperIndex(index);
        setIsClipperModalOpen(true);        
        setCurrentClipper(clientClipper.clipper);

        if (homeContext.pageLocation === PageLocation.CLIENT_EDIT && clientContext.client.id > 0 && clientClipper.editAction == EditAction.NONE)
            fetchClippingParametersForClipper(clientClipper);
        else
            setCurrentClientClipper(clientClipper);
    };

    const handleClipperTableDelete = (index: number) => {
        const clientClippers = JSON.parse(JSON.stringify(clientClippersForTable));

        if (clientClippers[index].editAction == EditAction.INSERT)
            clientClippers.splice(index, 1);
        else
            clientClippers[index].editAction = EditAction.DELETE;

        clientContext.setClientClippers(clientClippers);
        setIsConfirmDeletionModalOpen(false);
        setCurrentClientClipperIndex(-1);
    };

    const getAddNewButton = () => {
        return (
            <Grid item>
                <Button
                    onClick={() => setIsClipperModalOpen(true)}
                    startIcon={<Icon color='primary'>add</Icon>}>
                    Adicionar Clipadora
                </Button>
            </Grid>
        );
    };

    const getResultTable = () => {
        if (!clientClippersForTable.length || clientClippersForTable.length == 0) {
            return(
                <Grid item xs={12} className={classes.cardHeight}>
                    <TableEmpty 
                        displayText="Nenhuma clipadora cadastrada" 
                        cssProperties={{ minHeight: '150px' }} /> 
                </Grid>
            );
        } else {
            return (
                <Grid item xs={12}>
                    <TableCommon
                        tableData={clientClippersForTable}
                        tableColumns={clippersTableColumns}
                        tableActions={clippersTableActions}
                        extraOptions={clippersTableOptions}
                        emptyDataSourceMessage='Nenhuma clipadora cadastrada' />
                </Grid>
            );
        }
    };

    return(
        <ClientClipperContext.Provider value={{
            currentClientClipperIndex, setCurrentClientClipperIndex,
            clipperList, setClipperList,
            currentClipper, setCurrentClipper,
            currentClientClipper, setCurrentClientClipper,
            isClipperModalOpen, setIsClipperModalOpen,
            clipperEditMode, setClipperEditMode,
        }}>
            <Grid container direction="column" spacing={1}>  
                <Grid item xs={12}>
                    <Typography variant="subtitle1">Clipadoras</Typography>
                </Grid>
                { getAddNewButton() }
                { getResultTable() }
            </Grid>

            <ClientClipperModal showDialog={isClipperModalOpen} setShowDialog={setIsClipperModalOpen} />
           
            <BasicConfirmDialog
                title="Excluir clipadora?"
                description="Ao excluir esta clipadora, ela não ficará mais disponível para futuras classificações."
                handleFirstButton={() => handleClipperTableDelete(currentClientClipperIndex)}
                handleSecondButton={() => setIsConfirmDeletionModalOpen(false)}
                textFirstButton="Excluir"
                textSecondButton="Voltar"
                reverseButtons={true}
                cssProperties={{ justifyButtons: 'flex-end' }}
                open={isConfirmDeletionModalOpen} />
        </ClientClipperContext.Provider>
    );
}